import type { CombinedError } from '@urql/core';
import type { Ref } from 'vue';

import { setPasswordMutation } from '~/graphql/documents/core';
import { findPasswordChangeUnverifiedError } from '~/utils/unified/passwordChange';

interface SetPasswordWithTokenOptions {
  /** Owned by the surface holding the step, so its card and button stay in step with the request. */
  loading: Ref<boolean>;
  /** The single-use change token, given up here the moment the server says it is spent. */
  token: Ref<string | null>;
  /** The surface's own words for a spent token — an educator is not sent to a student profile. */
  tokenExpiredMessage: () => string;
  onSaved: () => void;
}

/**
 * Spends a single-use password change token, shared by every surface that offers a first
 * password without a second emailed code. The caller keeps its own analytics and its own way
 * on from the step; everything about the token itself lives here.
 */
export const useSetPasswordWithToken = ({ loading, token, tokenExpiredMessage, onSaved }: SetPasswordWithTokenOptions) => {
  const client = useUrqlClient();
  const { fetchUser } = useAuth();

  const expiredTokenMessage = ref<string | null>(null);

  const setPassword = async (newPassword: string): Promise<{ error: CombinedError | string | null }> => {
    if (loading.value) {
      return { error: null };
    }

    /**
     * A token given up to expiry leaves the step with nothing to send. Repeating the explanation
     * keeps it on screen rather than clearing it the moment they try again.
     */
    if (!token.value) {
      return { error: expiredTokenMessage.value };
    }

    loading.value = true;
    const result = await client.mutation(setPasswordMutation, {
      input: { changeToken: token.value, newPassword },
    });

    if (result.error) {
      loading.value = false;
      /**
       * The token runs on its own short clock, and a spent one is the single refusal nothing on
       * this step can re-verify — no code was emailed here to enter. Give the token up and
       * explain it, leaving the skip as the way on.
       */
      if (findPasswordChangeUnverifiedError(result.error)) {
        token.value = null;
        expiredTokenMessage.value = tokenExpiredMessage();
        return { error: expiredTokenMessage.value };
      }
      return { error: result.error };
    }

    try {
      await fetchUser(true);
    }
    catch {
      // The password is saved either way; the refetch only freshens `hasPassword` for this session.
    }
    loading.value = false;
    onSaved();
    return { error: null };
  };

  return { expiredTokenMessage, setPassword };
};
